import 'react-photo-view/dist/react-photo-view.css';

import { Progress } from 'antd';
import React, { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useScreenshotsReport } from '@/hooks/influencer';

import { Check, Download } from 'react-feather';
import { useUnifiedCloudinaryUpload, CloudinaryFile } from '@/hooks';
import { FileUploader } from '@/components/file-uploader';
import { MediaItem } from '@/components/media-item';
import { Button, Link } from '@/components';
import { ScreenshotsReportType } from '@/hooks/influencer/useScreenshotsReport';
import { getMediaThumbnailUrl, getVideoPosterUrl } from '@/utils/mediaUtils';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import type { OverlayRenderProps } from 'react-photo-view/dist/types';
import styles from './CollaborationItem.module.css';

function getFileType(url: string) {
  const fileExtension = (url.split('.') as TypeAny).pop().toLowerCase();
  const videoExtensions = ['mp4', 'avi', 'mov', 'webm'];

  if (videoExtensions.includes(fileExtension)) return 'video';

  return 'img';
}

function slideshowOverlay(overlay: OverlayRenderProps, t: TypeAny) {
  if (!overlay.images[overlay.index].src) return null;

  const overlayFile = overlay.images[overlay.index].src as string;

  const overlayFileType = getFileType(overlayFile);

  return (
    <>
      {overlayFileType === 'video' && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          key={`report-overlay-${overlay.index}`}
          style={{
            background: 'var(--color-black)',
            display: 'block',
            height: '100%',
            position: 'absolute',
            width: '100%',
            zIndex: 10
          }}
          controls
          playsInline
          poster={getVideoPosterUrl(overlayFile)}
        >
          <source src={overlayFile} type="video/mp4" />
        </video>
      )}

      <div
        style={{
          bottom: '4.5rem',
          padding: '0.5rem',
          position: 'fixed',
          right: 0,
          zIndex: 11
        }}
      >
        <Link href={overlayFile} theme="cta" target="_blank">
          <span
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: '0.5rem'
            }}
          >
            {t('carousel.download')}
            <Download size={16} />
          </span>
        </Link>
      </div>
    </>
  );
}

interface ReportProps {
  report: Api.Influencer.Screenshot;
  step?: string;
  setStatus: (code: string) => void;
}

export function Report({ report, step, setStatus }: ReportProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [newSelectedFiles, setNewSelectedFiles] = useState<File[]>([]);
  const [showNewUploadSection, setShowNewUploadSection] = useState(false);

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { screenshotReport, isLoading } = useScreenshotsReport({
    id: report.request_id,
    onSuccess: (data: ScreenshotsReportType) => {
      if (['BEAT_FINISHED', 'COLLABORATION_FINISHED'].includes(data.code)) {
        setStatus(data.code);
      }

      if ('request' in data) {
        const key = `influencer-screenshots-${report.collaboration_id}-${report.beat.beat_id}-${step}`;
        queryClient.setQueryData([key, null], (oldItems: TypeAny) => {
          if (oldItems) {
            const res = oldItems.requests.map((item: Api.Influencer.Screenshot) =>
              item.request_id === data.request.request_id ? data.request : item
            );
            return { requests: res };
          }
          return { requests: [] };
        });
      }
    }
  });
  const { progress, isUploading, uploadedCount, uploadFiles } = useUnifiedCloudinaryUpload();

  const contentRef = useRef<HTMLDivElement>(null);

  const getMediaArray = () =>
    selectedFiles.map((file) => ({
      file,
      isRaw: true
    }));

  const getNewMediaArray = () =>
    newSelectedFiles.map((file) => ({
      file,
      isRaw: true
    }));

  const handleSubmit = () => {
    uploadFiles(getMediaArray(), async (files: CloudinaryFile[]) => {
      await screenshotReport(
        files.map((file) => ({
          ...file,
          cloudinary_id: file.public_id
        }))
      );
      setSelectedFiles([]);
    });
  };

  const handleSubmitAgain = () => {
    setShowNewUploadSection(true);
  };

  const handleCancelNewUpload = () => {
    setShowNewUploadSection(false);
    setNewSelectedFiles([]);
  };

  const handleSubmitNewScreenshots = () => {
    uploadFiles(getNewMediaArray(), async (files: CloudinaryFile[]) => {
      await screenshotReport(
        files.map((file) => ({
          ...file,
          cloudinary_id: file.public_id
        }))
      );
      setNewSelectedFiles([]);
      setShowNewUploadSection(false);
    });
  };

  return (
    <div className={`${styles.draftWrap} ${styles.expand}`}>
      <div className={styles.header}>
        <div className={styles.headerName}>{report.product_name}</div>
      </div>
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.data} ref={contentRef}>
            {report.approved_draft?.medias.length && (
              <div className={styles.dataRow}>
                <div className={styles.rowInfo}>
                  <div className={styles.infoTitle}>
                    {t('screenshot.publishedMaterials')} <span>*</span>
                  </div>
                </div>
                <div className={styles.rowContent}>
                  <PhotoProvider overlayRender={(overlay) => slideshowOverlay(overlay, t)}>
                    {report.approved_draft?.medias
                      .filter((media) => !media.is_raw)
                      .map((m) => (
                        <PhotoView key={m.url} src={m.url}>
                          <MediaItem key={m.url} src={getMediaThumbnailUrl(m)} />
                        </PhotoView>
                      ))}
                  </PhotoProvider>
                </div>
              </div>
            )}

            <div className={styles.dataRow}>
              <div className={styles.rowInfo}>
                <div className={styles.infoTitle}>
                  {report.status === 'REPORTED'
                    ? t('screenshot.reportedScreenshots')
                    : t('screenshot.reportScreenshots')}{' '}
                  <span>*</span>
                </div>
                <div className={styles.infoDescription}>{t('screenshot.uploadTooltip')}</div>
              </div>
              <div className={styles.rowContent}>
                {report.status === 'REPORTED' && (
                  <PhotoProvider overlayRender={(overlay) => slideshowOverlay(overlay, t)}>
                    {report.medias.map((m) => (
                      <PhotoView key={m.url} src={m.url}>
                        <MediaItem sizeSm key={m.url} src={getMediaThumbnailUrl(m)} />
                      </PhotoView>
                    ))}
                  </PhotoProvider>
                )}
                {report.status === 'REPORT_NOW' && (
                  <FileUploader
                    sizeSm
                    files={selectedFiles}
                    onFilesChange={(filesData) => setSelectedFiles(filesData)}
                  />
                )}
              </div>
            </div>

            {report.status === 'REPORTED' && showNewUploadSection && (
              <div className={styles.dataRow}>
                <div className={styles.rowInfo}>
                  <div className={styles.infoTitle}>
                    {t('screenshot.reportNewScreenshots')} <span>*</span>
                  </div>
                  <div className={styles.infoDescription}>
                    {t('screenshot.uploadNewScreenshotsTooltip')}
                  </div>
                </div>
                <div className={styles.rowContent}>
                  <FileUploader
                    sizeSm
                    files={newSelectedFiles}
                    onFilesChange={(filesData) => setNewSelectedFiles(filesData)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={`${styles.footer} ${styles.publicationFooter}`}>
          {report.status === 'REPORTED' ? (
            <div className={styles.buttonGroup}>
              <Button disabled isLoading={isLoading} className={styles.publishedButton}>
                {t('screenshot.screenshotSubmitted')}
                <Check size={18} />
              </Button>
              {!showNewUploadSection && (
                <Button disabled={isLoading} onClick={handleSubmitAgain}>
                  {t('screenshot.submitAgain')}
                </Button>
              )}
              {showNewUploadSection && (
                <>
                  <Button
                    theme="cta"
                    disabled={!newSelectedFiles.length || isLoading}
                    isLoading={isLoading}
                    onClick={handleSubmitNewScreenshots}
                  >
                    {t('progressBar.step3')}
                  </Button>
                  <Button disabled={isLoading} onClick={handleCancelNewUpload}>
                    {t('actions.cancel')}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <Button
              theme="cta"
              disabled={!selectedFiles.length}
              isLoading={isLoading}
              onClick={handleSubmit}
            >
              {t('progressBar.step3')}
            </Button>
          )}
        </div>
        {isUploading && (
          <div className={styles.loaderWrapper}>
            <p className={styles.loaderInfo}>
              {`${uploadedCount} / ${showNewUploadSection ? newSelectedFiles.length : selectedFiles.length} ${t('draft.filesComplete')}`}
            </p>
            <div className={styles.loaderProgress} style={{ width: '400px' }}>
              <Progress
                percent={progress}
                showInfo={false}
                status="active"
                strokeColor={{
                  '0%': '#7AD098',
                  '100%': '#87d068'
                }}
                size={{ height: 14 }}
              />
            </div>
            <p className={styles.loaderTitle}>{t('draft.uploadTitle')}</p>
            <p className={styles.loaderDescription}>{t('draft.uploadSubtitle')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
